import React, { useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import InsertLinkIcon from "@material-ui/icons/InsertLink";
import FormatClearIcon from "@material-ui/icons/FormatClear";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    "&:focus-within": { borderColor: theme.palette.primary.main },
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    padding: "2px 4px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  editor: {
    minHeight: 120,
    maxHeight: 320,
    overflowY: "auto",
    padding: "10px 12px",
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    outline: "none",
    "& p": { margin: "0 0 8px" },
    "& ul, & ol": { margin: "0 0 8px", paddingLeft: 22 },
    "& blockquote": {
      margin: "0 0 8px",
      paddingLeft: 10,
      borderLeft: `3px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
    },
    "& a": { color: theme.palette.primary.main },
    "&:empty:before": { content: "attr(data-placeholder)", color: theme.palette.text.disabled },
  },
}));

const ACCIONES = [
  { cmd: "bold", icon: FormatBoldIcon, key: "bold" },
  { cmd: "italic", icon: FormatItalicIcon, key: "italic" },
  { cmd: "underline", icon: FormatUnderlinedIcon, key: "underline" },
  { cmd: "insertUnorderedList", icon: FormatListBulletedIcon, key: "bullets" },
  { cmd: "insertOrderedList", icon: FormatListNumberedIcon, key: "numbers" },
  { cmd: "formatBlock", arg: "blockquote", icon: FormatQuoteIcon, key: "quote" },
];

/**
 * Editor de correo con formato (doc 0449 §4.4): negrita, cursiva, subrayado, listas, cita y enlaces.
 * Devuelve el HTML y su texto plano. El HTML NO se confía: el backend lo sanea con una lista blanca
 * (`emailHtmlSanitizer`). Al pegar entra como texto plano para no arrastrar el formato de Word o de
 * una web, que el saneado descartaría de todos modos.
 */
const EmailRichEditor = ({ onChange, placeholder, toolbarEnd }) => {
  const classes = useStyles();
  const ref = useRef(null);

  const emitir = () => {
    const el = ref.current;
    if (!el) return;
    const texto = el.innerText.replace(/ /g, " ").trim();
    onChange(texto ? el.innerHTML : "", texto);
  };

  const ejecutar = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emitir();
  };

  const enlazar = () => {
    const url = window.prompt(i18n.t("emailEditor.linkPrompt"), "https://");
    if (!url || !/^(https?:\/\/|mailto:)/i.test(url.trim())) return;
    ejecutar("createLink", url.trim());
  };

  const pegar = (e) => {
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    emitir();
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        {ACCIONES.map(({ cmd, arg, icon: Icon, key }) => (
          <Tooltip key={key} title={i18n.t(`emailEditor.${key}`)}>
            <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => ejecutar(cmd, arg)}>
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        ))}
        <Tooltip title={i18n.t("emailEditor.link")}>
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={enlazar}>
            <InsertLinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={i18n.t("emailEditor.clear")}>
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => ejecutar("removeFormat")}>
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* Acciones del que lo usa (p. ej. el clip de adjuntar), al final de la barra como en un cliente de correo. */}
        {toolbarEnd && <span style={{ marginLeft: "auto", display: "inline-flex" }}>{toolbarEnd}</span>}
      </div>
      <div
        ref={ref}
        className={classes.editor}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        onInput={emitir}
        onPaste={pegar}
      />
    </div>
  );
};

export default EmailRichEditor;
